import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { ApiServiceErr, axiosApi } from "./apiService";

export interface CommonValues {
  key?: string;
  indicator?: string;
  measure?: number;
  unit?: string;
}

export interface CycleStages {
  cultivationImpact?: string;
  processImpact?: string;
  transportImpact?: string;
  storageImpact?: string;
}

interface ProcessedProductSummary {
  productDetail: any;
  productName: string;
  totalImpact: CommonValues[];
  climateChange: CommonValues;
  cycleStages: CycleStages;
  errors: string[];
  warnings: string[];
}

export interface CommonValuesHistory {
  key?: string;
  indicator?: string;
  measure?: number;
  unit?: string;
  createdDate?: Date;
}

export interface ProductSummaryHistory {
  totalImpact: CommonValuesHistory[];
  climateChange: CommonValuesHistory;
  cycleStages: CycleStages;
}

interface ProcessedProductSummaryHistory {
  productDetail: any;
  productName: string;
  productSummaryHistory: ProductSummaryHistory[];
}

interface ProcessedProductSummaryOptions {
  pageNumber?: number;
  pageSize?: number;
  productId?: string;
  salesUnitId?: string;
  isSupplier?: boolean;
  productIsAverage?: boolean;
}

export const useGetProductSummary = ({
  productId,
  salesUnitId,
  isSupplier = true,
  productIsAverage = false
}: ProcessedProductSummaryOptions) =>
  useQuery<ProcessedProductSummary, ApiServiceErr>(
    [productId, salesUnitId],
    async () => {
      const response = await axiosApi.get("/product-summary/by-id", {
        params: { productId, salesUnitId }
      });
      return response.data;
    },
    {
      enabled: !!productId && !!salesUnitId && isSupplier && !productIsAverage
    }
  );

export const useGetAverageProductSummary = (
  { productId, productIsAverage }: ProcessedProductSummaryOptions,
  options?: UseQueryOptions<any, ApiServiceErr>
) =>
  useQuery<ProcessedProductSummary, ApiServiceErr>(
    ["average-product-summary", productId],
    async () => {
      const response = await axiosApi.get(`v2/products/average-summary/${productId}`);
      return response.data;
    },
    {
      // Only enable when both productId exists AND productIsAverage is true
      enabled: !!productId && !!productIsAverage,
      ...options
    }
  );

export const useGetProductSummariesForNodes = (productSummaryId: string) =>
  useQuery<any, ApiServiceErr>(
    [productSummaryId, "useGetProductSummariesForNodes"],
    async () => {
      const response = await axiosApi.get("/product-summary/node-summary-impacts", {
        params: { productSummaryId }
      });
      return response.data || {};
    },
    {
      enabled: !!productSummaryId
    }
  );

export const useGetProductSummaryHistory = ({
  pageNumber,
  pageSize,
  productId,
  salesUnitId,
  isSupplier = true
}: ProcessedProductSummaryOptions) =>
  useQuery<any, ApiServiceErr>(
    [productId, salesUnitId, pageNumber, pageSize, "useGetProductSummaryHistory"],
    async () => {
      const response = await axiosApi.get("/product-summary/history", {
        params: { pageNumber, pageSize, productId, salesUnitId }
      });

      return response.data;
    },
    {
      enabled: !!productId && !!salesUnitId && isSupplier
    }
  );

export const useGetProductSummaryHistoryDropDownData = ({
  productId,
  salesUnitId,
  isSupplier = true,
  productIsAverage
}: ProcessedProductSummaryOptions) =>
  useQuery<any, ApiServiceErr>(
    [productId, salesUnitId, "useGetProductSummaryHistory"],
    async () => {
      const response = await axiosApi.get("/product-summary/history/drop-down", {
        params: { productId, salesUnitId }
      });

      return response.data;
    },
    {
      enabled:
        !!productId &&
        !!salesUnitId &&
        isSupplier &&
        !productIsAverage &&
        productIsAverage !== undefined
    }
  );

export const useGetProductSummaryHistoryById = (
  { productSummaryId, productIsAverage }: { productSummaryId: string; productIsAverage?: boolean },
  options?: UseQueryOptions<any, ApiServiceErr>
) =>
  useQuery<any, ApiServiceErr>(
    ["product-summary-history", productSummaryId],
    async () => {
      const response = await axiosApi.get(`/product-summary/history/${productSummaryId}`);
      return response.data;
    },
    {
      // Only enable when productSummaryId exists AND it's not an average product
      enabled: !!productSummaryId && !productIsAverage,
      ...options
    }
  );

export const useGenerateBestPractices = (opt?: UseMutationOptions) =>
  useMutation<any, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post(`/ai/best-practices`, {
      drawingId: "65724949cf878d56e2cc7644"
    });
    return response?.data;
  }, opt);
